import React from 'react';
import { Card, QRCode, Button, message, Image } from 'antd';
import { ShopOutlined, DownloadOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { toPng } from 'html-to-image';
import {
  IoChevronBackOutline,
  IoSearchOutline,
  IoQrCodeOutline,
  IoCellularOutline,
  IoWifiOutline,
  IoBatteryFull,
  IoChatbubbleOutline,
  IoCallOutline,
  IoBookOutline,
  IoShareOutline,
  IoBriefcaseOutline,
  IoShieldCheckmarkOutline,
  IoInformationCircleOutline,
  IoMailOutline,
  IoLocationOutline,
  IoGlobeOutline,
} from 'react-icons/io5';

const formatVerticalLabel = (value) =>
  String(value || '')
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');

const truncateText = (text, limit = 60) =>
  String(text || '').length > limit ? String(text).substring(0, limit) + '...' : text;

const actionCardStyle = {
  flex: 1,
  background: '#fff',
  border: '1px solid #f0f0f0',
  borderRadius: '12px',
  padding: '10px 4px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const actionIconStyle = {
  fontSize: '18px',
  color: '#0B3C2C',
};

const actionLabelStyle = {
  fontSize: '11px',
  fontWeight: '500',
  color: '#0B3C2C',
};

const detailItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 20px',
  gap: '16px',
};

const detailIconStyle = {
  fontSize: '20px',
  color: '#666',
  flexShrink: 0,
};

const detailTextStyle = {
  fontSize: '13px',
  color: '#333',
  lineHeight: '1.4',
  wordBreak: 'break-word',
};

const WHATSAPP_LOGO_BASE64 = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjMjVEMzY2IiBkPSJNMTIuMDA0IDJjLTUuNTEgMC05Ljk5MyA0LjQ4My05Ljk5MyA5Ljk5M2E5LjkzIDkuOTMgMCAwIDAgMS41NDEgNS4yNThsLTEuMDQ3IDMuODIgMy45MTMtMS4wMjZhOS45MDcgOS45MDcgMCAwIDAgNS41ODYgMS42OTZjNS41MSAwIDkuOTkzLTQuNDgzIDkuOTkzLTkuOTkzYzAtNS41MS00LjQ4My05Ljk5My05Ljk5My05Ljk5M201LjI0MiAxNC4yOGMtLjIyLjYxOC0xLjI5IDEuMjA2LTEuNzgyIDEuMjYyYy0uNDguMDU1LS45OC4wNzctMy4wOS0uNzVjLTIuNjk1LTEuMDU0LTQuNDI1LTMuOC00LjU2LTMuOThjLS4xMzUtLjE4LTEuMDk2LTEuNDU4LTEuMDk2LTIuNzgzYzAtMS4zMjUuNjktMS45NzcuOTM1LTIuMjRjLjI0NS0uMjYyLjUzNS0uMzI4LjcxLS4zMjhjLjE3OCAwIC4zNTcuMDAxLjUxMi4wMDljLjE2Mi4wMDcuMzgyLS4wNjIuNTk3LjQ1N2MuMjIuNTM1Ljc1MyAxLjg0LjgxOSAxLjk3MmMuMDY1LjEzLjExLjI4NS4wMi40NTdjLS4wODUuMTc2LS4xMy4yODUtLjI2LjQzOGMtLjEzLjE1My0uMjc1LjM0Mi0uMzkuNDZjLS4xMy4xMy0uMjY1LjI3LS4xMTUuNTI2Yy4xNS4yNTYuNjY2IDEuMSAxLjQzIDEuNzgyYy45ODMuODc2IDEuODEgMS4xNDcgMi4wNyAxLjI4Yy4yNTguMTMuNDEuMTEuNTYtLjA2MmMuMTUtLjE3NS42NDUtLjc1My44Mi0xLjAxYy4xNy0uMjU3LjM0NS0uMjIuNTgtLjEzYy4yMy4wOSAxLjQ3LjY5NCAxLjcyMy44MmMuMjUuMTI3LjQyLjE5LjQ4LjI5M2MuMDYuMTA0LjA2LjYwNC0uMTYgMS4yMjIiLz48L3N2Zz4=";

const ProfilePreview = ({ profile }) => {
  const picture = profile?.profile_picture_url;
  const name = profile?.display_name || 'Business Name';
  const phone = profile?.phone_number || '+91 80005 65555';

  const [currentTime, setCurrentTime] = React.useState('');
  const [showQrModal, setShowQrModal] = React.useState(false);

  React.useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      setCurrentTime(`${hours}:${minutes}`);
    };
    updateTime();
    const timer = setInterval(updateTime, 60000);
    return () => clearInterval(timer);
  }, []);

  const qrCardRef = React.useRef(null);

  const downloadQrCard = () => {
    if (!qrCardRef.current) return;

    const hideLoading = message.loading('Generating card image...', 0);

    toPng(qrCardRef.current, {
      cacheBust: true,
      pixelRatio: 4, // 4x scale for high-resolution, crisp export
      style: {
        transform: 'scale(1)',
      },
    })
      .then((dataUrl) => {
        hideLoading();
        const link = document.createElement('a');
        link.download = `${name.replace(/\s+/g, '_')}_qr_card.png`;
        link.href = dataUrl;
        link.click();
        message.success('Card downloaded successfully!');
      })
      .catch((err) => {
        hideLoading();
        console.error('Failed to generate image:', err);
        message.error('Failed to download card image.');
      });
  };

  return (
    <Card
      title={
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1a3353' }}>Profile Preview</div>
          <div style={{ fontSize: '12px', fontWeight: '400', color: '#8798ad', marginTop: '2px', whiteSpace: 'normal' }}>
            This is how your profile appears to WhatsApp users.
          </div>
        </div>
      }
    >
      {/* Screen Container */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          border: '1px solid #e8e8e8',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          maxWidth: '320px',
          height: '670px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Mock QR Code Modal Overlay */}
        <AnimatePresence>
          {showQrModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setShowQrModal(false)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
              }}
            >
              {/* Wrapper with padding to capture overflowing avatar and box-shadow without clipping */}
              <div
                ref={qrCardRef}
                style={{
                  padding: '32px 10px 10px 10px',
                  background: 'transparent',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <motion.div
                  initial={{ scale: 0.9, y: 15, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  exit={{ scale: 0.9, y: 15, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '20px',
                    padding: '40px 16px 20px 16px',
                    width: '100%',
                    maxWidth: '240px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    position: 'relative',
                  }}
                >
                  {/* Overlapping Avatar at top */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-32px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 2,
                    }}
                  >
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        border: '3px solid #fff',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                        backgroundColor: '#f5f5f5',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {picture ? (
                        <img
                          src={picture}
                          alt="Profile"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <ShopOutlined style={{ fontSize: '28px', color: '#bfbfbf' }} />
                      )}
                    </div>
                  </div>

                  <h4 style={{ margin: '0 0 2px 0', fontSize: '15px', fontWeight: '600', color: '#111', textAlign: 'center' }}>
                    {name}
                  </h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: '11px', color: '#888', textAlign: 'center' }}>
                    WhatsApp contact
                  </p>

                  {/* QR Code Container */}
                  <div className="qr-code-high-res-wrapper" style={{ width: 130, height: 130 }}>
                    {QRCode ? (
                      <QRCode
                        value={`https://wa.me/${String(phone || '').replace(/\D/g, '')}`}
                        size={520} // 4x larger drawing buffer for high-res rendering
                        bordered={false}
                        errorLevel="H"
                        icon={WHATSAPP_LOGO_BASE64}
                        iconSize={96} // Scale icon size proportionally (24 * 4)
                        style={{ width: '100%', height: '100%' }}
                      />
                    ) : (
                      <div style={{ width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eee', fontSize: '10px' }}>
                        QR Code Placeholder
                      </div>
                    )}
                    <style>{`
                      .qr-code-high-res-wrapper > * {
                        width: 100% !important;
                        height: 100% !important;
                      }
                      .qr-code-high-res-wrapper canvas,
                      .qr-code-high-res-wrapper svg,
                      .qr-code-high-res-wrapper img:first-of-type {
                        width: 100% !important;
                        height: 100% !important;
                      }
                      .qr-code-high-res-wrapper .ant-qrcode-mask img,
                      .qr-code-high-res-wrapper img:not(:first-of-type) {
                        width: 24px !important;
                        height: 24px !important;
                      }
                    `}</style>
                  </div>
                </motion.div>
              </div>

              {/* Download Button outside the captured card container */}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadQrCard}
                style={{
                  marginTop: '16px',
                  backgroundColor: '#0b3c2c',
                  borderColor: '#0b3c2c',
                  borderRadius: '10px',
                  height: '38px',
                  fontSize: '13px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                  zIndex: 101,
                }}
              >
                Download QR Card
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Status Bar */}
        <div
          style={{
            height: '34px',
            padding: '0 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px',
            color: '#fff',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9,
            pointerEvents: 'none',
          }}
        >
          <span style={{ fontWeight: '600' }}>{currentTime || '9:41'}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <IoCellularOutline style={{ fontSize: '12px' }} />
            <IoWifiOutline style={{ fontSize: '12px' }} />
            <IoBatteryFull style={{ fontSize: '14px' }} />
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Dark Green Header Area */}
          <div
            style={{
              background: '#0B3C2C',
              padding: '44px 16px 48px 16px',
              position: 'relative',
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {/* Header Action Icons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'absolute',
                top: '34px',
                left: '16px',
                right: '16px',
                color: '#fff',
              }}
            >
              <IoChevronBackOutline style={{ fontSize: '20px', cursor: 'pointer' }} />
              <div style={{ display: 'flex', gap: '16px' }}>
                <IoSearchOutline style={{ fontSize: '20px', cursor: 'pointer' }} />
                <IoQrCodeOutline
                  style={{ fontSize: '20px', cursor: 'pointer' }}
                  onClick={() => setShowQrModal(true)}
                />
              </div>
            </div>

            {/* Centered Avatar Frame */}
            <div
              style={{
                position: 'absolute',
                bottom: '-44px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  border: '4px solid #fff',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  backgroundColor: '#f5f5f5',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {picture ? (
                  <Image
                    src={picture}
                    alt="Profile"
                    width="100%"
                    height="100%"
                    style={{ objectFit: 'cover' }}
                  />
                ) : (
                  <ShopOutlined style={{ fontSize: '40px', color: '#bfbfbf' }} />
                )}
              </div>
            </div>
          </div>

          {/* Profile Content Body */}
          <div
            style={{
              background: '#fff',
              paddingTop: '52px',
              paddingBottom: '20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Business Name and Phone */}
            <div style={{ textAlign: 'center', padding: '0 16px' }}>
              <h3
                style={{
                  margin: '0 0 4px 0',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#000',
                }}
              >
                {name}
              </h3>
              <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>{phone}</span>
            </div>

            {/* Action Buttons Row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '20px 16px 12px 16px',
                gap: '8px',
              }}
            >
              <div style={actionCardStyle}>
                <IoChatbubbleOutline style={actionIconStyle} />
                <span style={actionLabelStyle}>Message</span>
              </div>
              <div style={actionCardStyle}>
                <IoCallOutline style={actionIconStyle} />
                <span style={actionLabelStyle}>Call</span>
              </div>
              <div style={actionCardStyle}>
                <IoBookOutline style={actionIconStyle} />
                <span style={actionLabelStyle}>Catalog</span>
              </div>
              <div style={actionCardStyle}>
                <IoShareOutline style={actionIconStyle} />
                <span style={actionLabelStyle}>Share</span>
              </div>
            </div>

            {/* Spacer Bar */}
            <div
              style={{
                height: '8px',
                backgroundColor: '#f7f9fa',
                margin: '8px 0',
                borderTop: '1px solid #f0f0f0',
                borderBottom: '1px solid #f0f0f0',
              }}
            />

            {/* Details List */}
            <div style={{ padding: '4px 0' }}>
              {/* Category */}
              {profile?.vertical && (
                <div style={detailItemStyle}>
                  <IoBriefcaseOutline style={detailIconStyle} />
                  <div style={detailTextStyle}>{formatVerticalLabel(profile.vertical)}</div>
                </div>
              )}

              {/* Description */}
              {profile?.description && (
                <div style={detailItemStyle}>
                  <IoShieldCheckmarkOutline style={detailIconStyle} />
                  <div style={detailTextStyle}>{truncateText(profile.description)}</div>
                </div>
              )}

              {/* About */}
              {profile?.about && (
                <div style={detailItemStyle}>
                  <IoInformationCircleOutline style={detailIconStyle} />
                  {/* <div style={detailTextStyle}>{profile.about}</div> */}
                  <div style={detailTextStyle}>{truncateText(profile.about)}</div>
                </div>
              )}

              {/* Email */}
              {profile?.email && (
                <div style={detailItemStyle}>
                  <IoMailOutline style={detailIconStyle} />
                  <div style={detailTextStyle}>{profile.email}</div>
                </div>
              )}

              {/* Address */}
              {profile?.address && (
                <div style={detailItemStyle}>
                  <IoLocationOutline style={detailIconStyle} />
                  <div style={detailTextStyle}>{truncateText(profile.address)}</div>
                </div>
              )}

              {/* Websites */}
              {(profile?.websites || []).map((site) => (
                <div key={site} style={detailItemStyle}>
                  <IoGlobeOutline style={detailIconStyle} />
                  <a
                    href={site.startsWith('http') ? site : `https://${site}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...detailTextStyle,
                      color: '#0066cc',
                      textDecoration: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {site}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Info Warning Alert Banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '14px 16px',
          borderRadius: '12px',
          backgroundColor: '#edf7ed',
          color: '#1e4620',
          marginTop: '20px',
          fontSize: '13px',
          lineHeight: '1.4',
          border: '1px solid #d4eed8',
        }}
      >
        <IoInformationCircleOutline
          style={{ fontSize: '18px', color: '#2e7d32', flexShrink: 0, marginTop: '1px' }}
        />
        <div>Profile changes may take a few minutes to reflect for your customers.</div>
      </div>
    </Card>
  );
};

export default ProfilePreview;
